import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface TrialStatus {
  isTrialActive: boolean;
  hasActiveSubscription: boolean;
  trialEndsAt: string | null;
  daysRemaining: number | null;
  needsSubscription: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useTrialStatus(): TrialStatus {
  const { user, impersonatedUserId } = useAuth();
  const effectiveUserId = impersonatedUserId || user?.id;
  const [status, setStatus] = useState<Omit<TrialStatus, 'refresh'>>({
    isTrialActive: false,
    hasActiveSubscription: false,
    trialEndsAt: null,
    daysRemaining: null,
    needsSubscription: false,
    loading: true,
  });

  useEffect(() => {
    if (!effectiveUserId) {
      setStatus({
        isTrialActive: false,
        hasActiveSubscription: false,
        trialEndsAt: null,
        daysRemaining: null,
        needsSubscription: false,
        loading: false,
      });
      return;
    }

    checkTrialAndSubscription();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkTrialAndSubscription();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [effectiveUserId]);

  async function checkTrialAndSubscription() {
    if (!effectiveUserId) {
      console.log('No user ID available');
      return;
    }

    const timeout = setTimeout(() => {
      console.warn('[useTrialStatus] Loading timeout exceeded, forcing completion');
      setStatus(prev => ({ ...prev, loading: false }));
    }, 4000);

    try {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('trial_ends_at')
        .eq('id', effectiveUserId)
        .maybeSingle();

      if (profileError) {
        console.error('[useTrialStatus] Error fetching profile:', profileError.message);
      }

      const { data: subscription, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('status, stripe_subscription_id')
        .eq('user_id', effectiveUserId)
        .maybeSingle();

      if (subscriptionError) {
        console.error('[useTrialStatus] Error fetching subscription:', subscriptionError.message);
      }

      const hasActiveSubscription = subscription?.status === 'active';
      const trialEndsAt = profile?.trial_ends_at;

      let isTrialActive = false;
      let daysRemaining = null;

      if (trialEndsAt) {
        const trialEndDate = new Date(trialEndsAt);
        const now = new Date();
        isTrialActive = trialEndDate > now;

        if (isTrialActive) {
          const diffTime = trialEndDate.getTime() - now.getTime();
          daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        }
      }

      const needsSubscription = !hasActiveSubscription && !isTrialActive;

      clearTimeout(timeout);
      setStatus({
        isTrialActive,
        hasActiveSubscription,
        trialEndsAt,
        daysRemaining,
        needsSubscription,
        loading: false,
      });
    } catch (error) {
      console.error('Error checking trial status:', error);
      clearTimeout(timeout);
      setStatus(prev => ({ ...prev, loading: false }));
    }
  }

  return {
    ...status,
    refresh: checkTrialAndSubscription,
  };
}
